import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import { DateTime } from 'luxon';
import React, { FC, useContext } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { keysToCamel } from '@utils/butter';
import { doesNotExist } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import { useAuth } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import { dateOfBirth, dateOfBirthName, queryName } from '@graphql/fragments/dateOfBirth';

import MedicareAttestationStep, {
	IMedicareAttestationStepPageContent,
} from './MedicareAttestationStep.component';

interface IMedicareAttestationStepPageProps extends IFlowStepProps {}

const FullWidthColumn = fullWidth(FlexColumn);
const AlignedRow = aligned(FlexRow);

const CheckboxSkeleton = styled(Skeleton)`
	flex-shrink: 0;
`;

const AttestationSkeleton = (
	<CenteredGridItem>
		<FullWidthColumn gap="lg">
			<Skeleton structure="column" repeated={2} height="lg" fullWidth />
			<Skeleton structure="column" repeated={4} height="md" fullWidth />
			<AlignedRow gap="sm" alignItems="flex-start">
				<CheckboxSkeleton height="md" width="md" />
				<Skeleton structure="column" repeated={6} height="sm" fullWidth />
			</AlignedRow>
		</FullWidthColumn>
	</CenteredGridItem>
);

const MedicareAttestationStepPage: FC<IMedicareAttestationStepPageProps> = ({
	path,
	stepKey,
	...props
}) => {
	const { loggedIn } = useAuth();

	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "medicare-attestation-step" }) {
				page_header
				page_info
				medicare_attestation_text
			}
		}
	`);

	const { existingFormData, handleFormSubmission, handleNavBack, handleNavNext } =
		useContext(FlowContext);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { data: dobData, loading: dobLoading } = useQuery<{
		authenticatedMember: { dateOfBirth: string };
	}>(combineMemberFragments([dateOfBirthName], [dateOfBirth], queryName), {
		client: graphQLv2Client,
		skip: !loggedIn,
	});

	useDeepCompareEffect(() => {
		if (dobLoading || doesNotExist(dobData?.authenticatedMember?.dateOfBirth)) {
			return;
		}

		const under65 =
			DateTime.fromISO(dobData?.authenticatedMember?.dateOfBirth ?? '')
				.diffNow('years')
				.negate().years < 65;
		if (under65) {
			props?.location?.state?.reverse ? handleNavBack(stepKey) : handleNavNext(stepKey);
		}
	}, [dobData, dobLoading]);

	const refinedContent: IMedicareAttestationStepPageContent = keysToCamel(
		pageContent
	) as IMedicareAttestationStepPageContent;

	return (
		<LoadingWrapper loading={dobLoading} skeleton={AttestationSkeleton}>
			<MedicareAttestationStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
			/>
		</LoadingWrapper>
	);
};

export default MedicareAttestationStepPage;
